// src/pages/About.jsx
import React, { useEffect } from "react";
import styles from "./About.module.css";
import imgDocente from "../assets/maestro.jpg";
import imgSesion from "../assets/alumno.jpg";

// Hook para animaciones on-scroll (usa data-reveal)
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add(styles.in); // activa transición
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

export default function About() {
  useReveal();

  return (
    <main className={styles.wrap} aria-labelledby="about-title">
      {/* ===== HERO SPLIT ===== */}
      <section className={`${styles.heroSplit} ${styles.revealUp}`} data-reveal>
        {/* Texto */}
        <div className={styles.copy}>
          <h1 id="about-title" className={styles.titleXL}>Sobre Nosotros</h1>

          <p className={styles.lead}>
            <strong>
              Nacimos con una misión clara: elevar el nivel de la educación en
              línea de nivel medio y superior en México.
            </strong>
          </p>

          <p className={styles.body}>
            Fundada por educadores y profesionales, <em>Osmar</em> es una
            plataforma de aprendizaje que ofrece <strong>Preparatoria</strong> y{" "}
            <strong>Licenciatura</strong> en modalidad 100% online. Creamos
            rutas formativas claras, modernas y accesibles para que cada
            estudiante avance a su ritmo sin sacrificar calidad académica.
          </p>

          <p className={styles.body}>
            Hoy, acompañamos a jóvenes y adultos que desean concluir la
            preparatoria, titularse o dar el salto profesional con herramientas
            prácticas, tutores reales y clases en vivo.
          </p>

          <p className={styles.body}>
            <strong>Seguimos creciendo contigo.</strong>
          </p>

          <a className={styles.btnOutline} href="/contact">
            Contáctanos
          </a>
        </div>

        {/* Imágenes */}
        <div className={`${styles.heroImages} ${styles.stagger}`} data-reveal>
          <figure className={`${styles.imgCard} ${styles.revealUp}`} data-reveal>
            <img src={imgDocente} alt="Docente en bata cruzando brazos" />
          </figure>
          <figure className={`${styles.imgCard} ${styles.revealUp}`} data-reveal>
            <img src={imgSesion} alt="Estudiante en clase en línea" />
          </figure>
        </div>
      </section>

      {/* ===== Misión / Visión ===== */}
      <section className={styles.mvSection}>
        <div className={styles.mvContainer}>
          <h2 className={`${styles.mvTitle} ${styles.revealUp}`} data-reveal>
            Nuestra Misión y Visión
          </h2>
          <p className={`${styles.mvSubtitle} ${styles.revealUp}`} data-reveal>
            Programas de Preparatoria y Licenciatura 100% en línea, con calidad académica y enfoque práctico.
          </p>

          <div className={styles.mvGrid}>
            {/* Misión */}
            <article className={`${styles.mvCard} ${styles.revealUp}`} data-reveal>
              <div className={styles.mvImageWrapper}>
                <img src={imgDocente} alt="Misión" className={styles.mvImage} />
                <div className={styles.mvTextBox}>
                  <h3>Misión</h3>
                  <p>
                    Brindar educación accesible y de alta calidad que potencie el desarrollo
                    académico y profesional de nuestros estudiantes.
                  </p>
                </div>
              </div>
            </article>

            {/* Visión */}
            <article className={`${styles.mvCard} ${styles.revealUp}`} data-reveal>
              <div className={styles.mvImageWrapper}>
                <img src={imgSesion} alt="Visión" className={styles.mvImage} />
                <div className={styles.mvTextBox}>
                  <h3>Visión</h3>
                  <p>
                    Ser la plataforma líder de educación en línea en México y Latinoamérica,
                    sinónimo de innovación, confianza y excelencia académica.
                  </p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ===== Video tipo “foto” + beneficios ===== */}
      <section className={styles.showcaseSection} aria-labelledby="feat-title">
        <div className={`${styles.showcaseContainer}`}>
          <div className={`${styles.showcaseGrid} ${styles.revealUp}`} data-reveal>
            {/* Izquierda: video */}
            <div className={styles.showMedia}>
              <div className={styles.showCard}>
                <video
                  className={styles.showVideo}
                  src={require("../assets/clase-en-linea.mp4")}
                  poster={require("../assets/clase-poster.jpg")}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            </div>

            {/* Derecha: contenido */}
            <div className={styles.showContent}>
              <h2 id="feat-title" className={styles.showTitle}>
                Educación en Línea de Calidad
              </h2>
              <p className={styles.showSubtitle}>
                Aprende desde cualquier lugar con clases interactivas, recursos digitales
                y el acompañamiento de profesores expertos.
              </p>

              <div className={`${styles.showFeatureList} ${styles.stagger}`} data-reveal>
                <div className={`${styles.showFeatureItem} ${styles.revealUp}`} data-reveal>
                  <span className={styles.showDot} aria-hidden />
                  <div>
                    <h3 className={styles.showFeatureTitle}>Clases en Vivo y Grabadas</h3>
                    <p className={styles.showFeatureDesc}>
                      Accede a sesiones en tiempo real o repite las lecciones cuando lo necesites.
                    </p>
                  </div>
                </div>

                <div className={`${styles.showFeatureItem} ${styles.revealUp}`} data-reveal>
                  <span className={styles.showDot} aria-hidden />
                  <div>
                    <h3 className={styles.showFeatureTitle}>Profesores Especializados</h3>
                    <p className={styles.showFeatureDesc}>
                      Aprende de profesionales con experiencia en la enseñanza online.
                    </p>
                  </div>
                </div>

                <div className={`${styles.showFeatureItem} ${styles.revealUp}`} data-reveal>
                  <span className={styles.showDot} aria-hidden />
                  <div>
                    <h3 className={styles.showFeatureTitle}>Plataforma Intuitiva</h3>
                    <p className={styles.showFeatureDesc}>
                      Materiales, evaluaciones y comunicación con un solo clic.
                    </p>
                  </div>
                </div>
              </div>

              <a className={styles.showCta} href="/contacto">Comenzar Ahora</a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Valores ===== */}
      <section className={styles.block} aria-labelledby="valores-title">
        <h2
          id="valores-title"
          className={`${styles.h2Center} ${styles.revealUp}`}
          data-reveal
        >
          Nuestros valores
        </h2>
        <div className={`${styles.values} ${styles.stagger}`} data-reveal>
          <article className={`${styles.value} ${styles.revealUp}`} data-reveal>
            <div className={styles.badge} aria-hidden />
            <h3 className={styles.h3}>Claridad</h3>
            <p className={styles.small}>
              Diseñamos rutas de estudio claras y procesos transparentes.
            </p>
          </article>
          <article className={`${styles.value} ${styles.revealUp}`} data-reveal>
            <div className={styles.badge} aria-hidden />
            <h3 className={styles.h3}>Responsabilidad</h3>
            <p className={styles.small}>
              Cumplimos lo que prometemos y medimos nuestro impacto educativo.
            </p>
          </article>
          <article className={`${styles.value} ${styles.revealUp}`} data-reveal>
            <div className={styles.badge} aria-hidden />
            <h3 className={styles.h3}>Aprendizaje</h3>
            <p className={styles.small}>
              Iteramos con datos y escuchamos a nuestros estudiantes.
            </p>
          </article>
          <article className={`${styles.value} ${styles.revealUp}`} data-reveal>
            <div className={styles.badge} aria-hidden />
            <h3 className={styles.h3}>Respeto</h3>
            <p className={styles.small}>
              Trabajamos con empatía y comunicación directa.
            </p>
          </article>
        </div>
      </section>

      {/* ===== Stats ===== */}
      <section
        className={`${styles.stats} ${styles.revealUp}`}
        data-reveal
        aria-label="Indicadores"
      >
        <div className={styles.statItem}>
          <span className={styles.statNumber}>+1,200</span>
          <span className={styles.statLabel}>Estudiantes</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statNumber}>35</span>
          <span className={styles.statLabel}>Programas</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statNumber}>25</span>
          <span className={styles.statLabel}>Profesores</span>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className={`${styles.cta} ${styles.revealScale}`} data-reveal aria-labelledby="cta-title">
        <div className={styles.ctaBox}>
          <h2 id="cta-title" className={styles.h2Cta}>
            ¿Hablamos?
          </h2>
          <p className={styles.textCta}>
            Cuéntanos tus metas y te ayudamos a convertirlas en un logro
            académico.
          </p>
          <a className={styles.button} href="/contacto">
            Contáctanos
          </a>
        </div>
      </section>
    </main>
  );
}
